import type {
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
  PuppyProjectModelDocument,
  PuppyProjectModelOccurrence,
} from '../../export/puppy/types'
import type { ModelAssetFile, ModelAssetNode } from '../assets/model-asset-types'
import { createMaterializedProjectModelNodeKey } from './project-model-node-identity'
import { assemblyTransformFromColumnMajorElements } from '../assembly-document/assembly-transform-matrix'

/**
 * 从离线包项目文档重建模型树的输入。
 */
export interface MaterializePuppyProjectModelTreeInput {
  /** OpenProject 映射得到的容器根节点。 */
  readonly root: ModelAssetNode
  /** 容器内装配语义文档，负责节点位姿。 */
  readonly assembly: PuppyAssemblyDocument
  /** 容器内项目 occurrence 文档，负责可编辑结构与版本引用。 */
  readonly projectModel: PuppyProjectModelDocument
  /** 承载 occurrence 显示、BRep、拓扑和 Mate 的根 `.puppy` 文件。 */
  readonly containerFile: ModelAssetFile
}

/**
 * 读取装配节点的有效本地矩阵。
 *
 * 项目文档不保存第二份 transform。缺失或损坏时必须拒绝恢复，避免逻辑树和显示树
 * 使用不同位姿继续污染删除、拖动和 Mate 求解。
 *
 * @param node 当前 assembly.json 节点。
 * @returns 可写入 ModelAssetNode 的行主序 16 元素矩阵。
 */
function readAssemblyNodeTransform(node: PuppyAssemblyNode): readonly number[] {
  const transform = node.transform
  if (!transform || transform.length !== 16 || transform.some((value) => !Number.isFinite(value))) {
    throw new Error(`PuppyCAD 项目 occurrence 缺少有效装配矩阵：assemblyNodeId=${node.nodeId}`)
  }
  return assemblyTransformFromColumnMajorElements(transform)
}

/**
 * 为包内 occurrence 创建版本身份。
 *
 * @param occurrence 当前 occurrence。
 * @returns 有后端版本引用时使用 repository:commit，否则使用明确的包内身份。
 */
function createOccurrenceVersionKey(occurrence: PuppyProjectModelOccurrence): string {
  const versionReference = occurrence.versionReference
  return versionReference
    ? `${versionReference.repositoryId}:${versionReference.commitId}`
    : `embedded-puppy:${occurrence.assemblyNodeId}`
}

/**
 * 从 v5 `.puppy` 项目文档重建可编辑 ModelAssetNode 树。
 *
 * @param input 容器根、装配语义、项目 occurrence 和容器文件。
 * @returns children 已由 project-model.json 恢复的不可变根节点。
 * @throws 当装配节点 ID 或项目 occurrence ID 重复时。
 * @throws 当项目根 occurrence 缺失、存在父节点或没有绑定装配根时。
 * @throws 当 occurrence 图存在循环、重复引用或引用未知装配节点时。
 * @throws 当 occurrence 图存在从根节点不可达的节点时。
 */
export function materializePuppyProjectModelTree(
  input: MaterializePuppyProjectModelTreeInput,
): ModelAssetNode {
  const assemblyNodeById = new Map<string, PuppyAssemblyNode>()
  input.assembly.nodes.forEach((node) => {
    if (assemblyNodeById.has(node.nodeId)) {
      throw new Error(`PuppyCAD 装配节点 ID 重复：${node.nodeId}`)
    }
    assemblyNodeById.set(node.nodeId, node)
  })
  const occurrenceById = new Map<string, PuppyProjectModelOccurrence>()
  const childOccurrencesByParentId = new Map<string, PuppyProjectModelOccurrence[]>()
  input.projectModel.occurrences.forEach((occurrence) => {
    if (occurrenceById.has(occurrence.occurrenceId)) {
      throw new Error(`PuppyCAD 项目 occurrence ID 重复：${occurrence.occurrenceId}`)
    }
    occurrenceById.set(occurrence.occurrenceId, occurrence)
    if (occurrence.parentOccurrenceId) {
      const children = childOccurrencesByParentId.get(occurrence.parentOccurrenceId) ?? []
      children.push(occurrence)
      childOccurrencesByParentId.set(occurrence.parentOccurrenceId, children)
    }
  })

  const rootOccurrence = occurrenceById.get(input.projectModel.rootOccurrenceId)
  if (!rootOccurrence) {
    throw new Error(`PuppyCAD 项目 occurrence 缺少根节点：${input.projectModel.rootOccurrenceId}`)
  }
  if (rootOccurrence.parentOccurrenceId !== null) {
    throw new Error(`PuppyCAD 项目 occurrence 根节点不能存在父节点：${rootOccurrence.occurrenceId}`)
  }
  if (rootOccurrence.assemblyNodeId !== input.assembly.rootNodeId) {
    throw new Error(
      `PuppyCAD 项目 occurrence 根绑定与装配根不一致：${rootOccurrence.assemblyNodeId}/${input.assembly.rootNodeId}`,
    )
  }

  const visitedOccurrenceIds = new Set<string>([rootOccurrence.occurrenceId])
  const materializeChildren = (
    parentOccurrenceId: string,
    parentNodeKey: string,
    depth: number,
  ): readonly ModelAssetNode[] => {
    const childOccurrences = childOccurrencesByParentId.get(parentOccurrenceId) ?? []
    return childOccurrences.map((occurrence) => {
      if (visitedOccurrenceIds.has(occurrence.occurrenceId)) {
        throw new Error(`PuppyCAD 项目 occurrence 图存在循环或重复引用：${occurrence.occurrenceId}`)
      }
      visitedOccurrenceIds.add(occurrence.occurrenceId)
      const assemblyNode = assemblyNodeById.get(occurrence.assemblyNodeId)
      if (!assemblyNode) {
        throw new Error(
          `PuppyCAD 项目 occurrence 引用未知装配节点：${occurrence.occurrenceId}/${occurrence.assemblyNodeId}`,
        )
      }
      const nodeKey = createMaterializedProjectModelNodeKey(parentNodeKey, occurrence.occurrenceId)
      const nodeDepth = depth + 1
      const versionReference = occurrence.versionReference
      const children = materializeChildren(occurrence.occurrenceId, nodeKey, nodeDepth)

      return {
        nodeKey,
        parentNodeKey,
        displayName: occurrence.name,
        instanceUuid: occurrence.instanceId ?? occurrence.occurrenceId,
        ...(occurrence.persistedNodeId ? { persistedNodeId: occurrence.persistedNodeId } : {}),
        depth: nodeDepth,
        repositoryId: versionReference?.repositoryId ?? '',
        branchId: versionReference?.branchId ?? '',
        commitId: versionReference?.commitId ?? '',
        versionKey: createOccurrenceVersionKey(occurrence),
        versionSwitchable: nodeDepth === 1 && Boolean(versionReference),
        nodeType: occurrence.nodeType,
        localTransform: readAssemblyNodeTransform(assemblyNode),
        boundingBox: [],
        assetSource: {
          kind: 'embedded-puppy-occurrence',
          occurrenceId: occurrence.occurrenceId,
          containerNodeKey: input.root.nodeKey,
          containerFile: input.containerFile,
          assemblyNodeId: occurrence.assemblyNodeId,
        },
        packageFiles: [],
        children,
      }
    })
  }

  const children = materializeChildren(rootOccurrence.occurrenceId, input.root.nodeKey, input.root.depth)
  if (visitedOccurrenceIds.size !== input.projectModel.occurrences.length) {
    const unreachableOccurrenceIds = input.projectModel.occurrences
      .map((occurrence) => occurrence.occurrenceId)
      .filter((occurrenceId) => !visitedOccurrenceIds.has(occurrenceId))
    throw new Error(`PuppyCAD 项目 occurrence 图存在不可达节点：${unreachableOccurrenceIds.join(',')}`)
  }

  return {
    ...input.root,
    children,
  }
}
